import React from 'react';
import {  
    Button,
    Box,
    styled,
    Typography,
    Container,
} from '@mui/material';
import { Link } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const GetInTouch = () => {
    const CTASection = styled(Box)(({ theme }) => ({
        padding: theme.spacing(12, 0),
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-10%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            right: '-5%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
        },
    }));

    const ContentBox = styled(Box)({
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
    });

    const MainTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        color: '#fff',
        marginBottom: theme.spacing(2),
        textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    }));

    const Description = styled(Typography)(({ theme }) => ({
        fontSize: '1.1rem',
        color: 'rgba(255, 255, 255, 0.9)',
        maxWidth: '700px',
        margin: '0 auto',
        marginBottom: theme.spacing(5),
        lineHeight: 1.8,
    }));

    const ButtonGroup = styled(Box)(({ theme }) => ({
        display: 'flex',
        gap: theme.spacing(3),
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginBottom: theme.spacing(6),
    }));

    const PrimaryButton = styled(Button)(({ theme }) => ({
        padding: '1rem 2.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '50px',
        background: '#FF6B35',
        color: '#fff',
        border: '2px solid transparent',
        boxShadow: '0 10px 30px rgba(255, 107, 53, 0.4)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            background: '#FF8C42',
            transform: 'translateY(-3px)',
            boxShadow: '0 15px 40px rgba(255, 107, 53, 0.5)',
        },
        '& .MuiButton-startIcon': {
            transition: 'transform 0.3s ease',
        },
        '&:hover .MuiButton-startIcon': {
            transform: 'rotate(15deg)',
        },
    }));

    const SecondaryButton = styled(Button)(({ theme }) => ({
        padding: '1rem 2.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '50px',
        background: 'transparent',
        color: '#fff',
        border: '2px solid #fff',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            background: '#fff',
            color: '#1e3a8a',
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 30px rgba(255, 255, 255, 0.3)',
        },
    }));

    const FeatureBox = styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(5),
        flexWrap: 'wrap',
        marginTop: theme.spacing(4),
    }));

    const Feature = styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        color: '#fff',
        '& svg': {
            fontSize: '2rem',
            color: '#FF6B35',
        },
        '& .feature-text': {
            fontSize: '1rem',
            fontWeight: 500,
        },
    }));

    return (
        <CTASection component="section">
            <Container maxWidth="lg">
                <ContentBox>
                    <MainTitle variant="h2">
                        Contact Us for 24/7 Support
                    </MainTitle>
                    
                    <Description>
                        We're dedicated to providing round-the-clock support. Whether you need assistance 
                        with equipment operation, maintenance, or have an emergency, our team is ready to 
                        assist you at any time.
                    </Description>

                    <ButtonGroup>
                        <PrimaryButton 
                            component={Link}
                            to="/contact"
                            variant="contained"
                            startIcon={<PhoneIcon />}
                        >
                            Get In Touch
                        </PrimaryButton>
                        
                        <SecondaryButton 
                            component={Link}
                            to="/about"
                            variant="outlined"
                        >
                            Learn More
                        </SecondaryButton>
                    </ButtonGroup>

                    <FeatureBox>
                        <Feature>
                            <AccessTimeIcon />
                            <span className="feature-text">24/7 Availability</span>
                        </Feature>
                        <Feature>
                            <PhoneIcon />
                            <span className="feature-text">Instant Response</span>
                        </Feature>
                    </FeatureBox>
                </ContentBox>
            </Container>
        </CTASection>
    );
};

export default GetInTouch;